import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "./ToolsTab.css";

const TOOL_TABS = [
  ["clearance", "Clairance rénale", "Renal clearance"],
  ["kfre", "KFRE", "KFRE"],
  ["ipss", "IPSS", "IPSS"],
  ["psaDensity", "Densité PSA", "PSA density"],
];

const IPSS_OPTIONS = [
  ["0 — Jamais", "0 — Not at all", "٠ — أبدًا", 0],
  ["1 — Moins d'une fois sur 5", "1 — Less than 1 in 5 times", "١ — أقل من مرة من كل خمس مرات", 1],
  ["2 — Moins de la moitié du temps", "2 — Less than half the time", "٢ — أقل من نصف الوقت", 2],
  ["3 — Environ la moitié du temps", "3 — About half the time", "٣ — نحو نصف الوقت", 3],
  ["4 — Plus de la moitié du temps", "4 — More than half the time", "٤ — أكثر من نصف الوقت", 4],
  ["5 — Presque toujours", "5 — Almost always", "٥ — دائمًا تقريبًا", 5],
];
const IPSS_QUESTIONS = [
  ["Sensation de vidange incomplète de la vessie", "Sensation of incomplete bladder emptying", "الشعور بعدم إفراغ المثانة بالكامل"],
  ["Besoin d'uriner à nouveau moins de 2 h après", "Need to urinate again within 2 hours", "الحاجة إلى التبول مرة أخرى خلال أقل من ساعتين"],
  ["Jet interrompu (s'arrête puis reprend)", "Intermittent stream (stops and starts)", "تقطع تدفق البول (يتوقف ثم يستأنف)"],
  ["Difficulté à retenir l'envie d'uriner (urgenturie)", "Difficulty postponing urination (urgency)", "صعوبة تأجيل التبول (الإلحاح البولي)"],
  ["Jet faible", "Weak urinary stream", "ضعف تدفق البول"],
  ["Besoin de pousser pour commencer à uriner", "Need to push or strain to begin urination", "الحاجة إلى الدفع لبدء التبول"],
  ["Nombre de levers nocturnes pour uriner", "Times getting up at night to urinate", "عدد مرات الاستيقاظ ليلًا للتبول"],
];
const NOCTURIA_OPTIONS = [
  ["0 — Aucun", "0 — None", "٠ — لا شيء", 0], ["1 fois", "1 time", "مرة واحدة", 1],
  ["2 fois", "2 times", "مرتان", 2], ["3 fois", "3 times", "٣ مرات", 3],
  ["4 fois", "4 times", "٤ مرات", 4], ["5 fois ou plus", "5 times or more", "٥ مرات أو أكثر", 5],
];
const QOL_OPTIONS = [
  ["0 — Très satisfait", "0 — Delighted", "٠ — سعيد جدًا", 0], ["1 — Satisfait", "1 — Pleased", "١ — سعيد", 1],
  ["2 — Plutôt satisfait", "2 — Mostly satisfied", "٢ — راضٍ إلى حد ما", 2], ["3 — Mitigé", "3 — Mixed", "٣ — متردد", 3],
  ["4 — Plutôt insatisfait", "4 — Mostly dissatisfied", "٤ — غير راضٍ إلى حد ما", 4], ["5 — Insatisfait", "5 — Unhappy", "٥ — غير راضٍ", 5],
  ["6 — Très insatisfait", "6 — Terrible", "٦ — غير راضٍ إطلاقًا", 6],
];
const numberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number.parseFloat(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
};
const round = (value, places = 1) => Number(value.toFixed(places));
const field = (value) => numberOrNull(value);
const calculateAge = (birthdate) => {
  if (!birthdate) return "";
  const match = String(birthdate).match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  const date = match
    ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
    : new Date(birthdate);
  if (Number.isNaN(date.getTime()) || date > new Date()) return "";
  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  if (
    today.getMonth() < date.getMonth() ||
    (today.getMonth() === date.getMonth() && today.getDate() < date.getDate())
  ) age -= 1;
  return age > 0 ? String(age) : "";
};
const normalizeSex = (gender) => {
  const normalized = String(gender || "").trim().toLowerCase();
  if (["male", "m", "man", "homme", "masculin"].includes(normalized)) return "male";
  if (["female", "f", "woman", "femme", "féminin", "feminin"].includes(normalized)) return "female";
  return "";
};
const sexOptions = (english) => [
  { value: "", label: "" },
  { value: "male", label: english ? "Male" : "Homme" },
  { value: "female", label: english ? "Female" : "Femme" },
];

function renalStage(gfr, english) {
  if (gfr === null || gfr < 0) return null;
  if (gfr >= 90) return { stage: "G1", band: "ok", text: english ? "Normal or high GFR" : "DFG normal ou élevé" };
  if (gfr >= 60) return { stage: "G2", band: "ok", text: english ? "Mildly decreased" : "Légèrement diminué" };
  if (gfr >= 45) return { stage: "G3a", band: "warn", text: english ? "Mild-to-moderate decrease" : "Diminution légère à modérée" };
  if (gfr >= 30) return { stage: "G3b", band: "warn", text: english ? "Moderate-to-severe decrease" : "Diminution modérée à sévère" };
  if (gfr >= 15) return { stage: "G4", band: "bad", text: english ? "Severely decreased" : "Sévèrement diminué" };
  return { stage: "G5", band: "bad", text: english ? "Kidney failure" : "Insuffisance rénale terminale" };
}

function calculateClearance(values, english) {
  const age = field(values.clAge);
  const weight = field(values.clWeight);
  const creatinine = field(values.clCreat);
  const sex = values.clSex;
  if (age === null || age <= 0 || weight === null || weight <= 0 || creatinine === null || creatinine <= 0 || !sex) return null;
  const creatUmol = values.clUnit === "mgL" ? creatinine * 8.84 : creatinine;
  const creatMgDl = creatUmol / 88.4;
  const rawCockcroft = ((sex === "male" ? 1.25 : 1.04) * weight * (140 - age)) / creatUmol;
  const cockcroft = Number.isFinite(rawCockcroft) && rawCockcroft >= 0 ? round(rawCockcroft) : null;
  let mdrd = 186 * creatMgDl ** -1.154 * age ** -0.203;
  if (sex === "female") mdrd *= 0.742;
  mdrd = round(mdrd);
  const female = sex === "female";
  const kappa = female ? 0.7 : 0.9;
  const alpha = female ? -0.241 : -0.302;
  const ratio = creatMgDl / kappa;
  let ckdEpi = 142 * Math.min(ratio, 1) ** alpha * Math.max(ratio, 1) ** -1.2 * 0.9938 ** age;
  if (female) ckdEpi *= 1.012;
  ckdEpi = round(ckdEpi);
  const stage = renalStage(ckdEpi, english);
  return {
    band: stage?.band || "ok",
    text: `${english ? "Cockcroft–Gault" : "Cockcroft–Gault"} ${cockcroft ?? "—"} mL/min · MDRD ${mdrd} · CKD-EPI 2021 ${ckdEpi} mL/min/1.73 m² — ${english ? "Stage" : "Stade"} ${stage?.stage}: ${stage?.text}.`,
  };
}

function calculateKfre(values, english) {
  const age = field(values.kfAge);
  const egfr = field(values.kfGfr);
  let acr = field(values.kfAcr);
  if (age === null || egfr === null || acr === null || !values.kfSex || egfr <= 0 || acr <= 0) return null;
  if (values.kfAcrUnit === "mgmmol") acr *= 8.84;
  const male = values.kfSex === "male" ? 1 : 0;
  const lp = -0.2201 * (age / 10 - 7.036) + 0.2467 * (male - 0.5642) -
    0.5567 * (egfr / 5 - 7.222) + 0.451 * (Math.log(acr) - 5.137);
  const e = Math.exp(lp);
  const risk2 = round((1 - 0.9832 ** e) * 100, 1);
  const risk5 = round((1 - 0.9365 ** e) * 100, 1);
  const band = risk2 >= 10 ? "bad" : risk5 >= 5 ? "warn" : "ok";
  const advice = risk2 >= 10
    ? (english ? "Prepare for kidney replacement therapy." : "Préparation à la suppléance rénale recommandée.")
    : risk5 >= 5
      ? (english ? "Nephrology referral recommended." : "Avis néphrologique recommandé.")
      : (english ? "Follow-up according to KDIGO stage." : "Suivi selon le stade KDIGO.");
  const validity = egfr >= 60
    ? (english ? " Validated for eGFR < 60 (stages G3–G5)." : " Équation validée pour DFG < 60 (stades G3–G5).")
    : "";
  return {
    band,
    text: `${english ? "Kidney failure risk" : "Risque d'insuffisance rénale terminale"}: ${risk2}% ${english ? "at 2 years" : "à 2 ans"}, ${risk5}% ${english ? "at 5 years" : "à 5 ans"}. ${advice}${validity}`,
  };
}

function calculateIpss(answers, qol, english) {
  if (answers.some((answer) => answer === null)) return null;
  const total = answers.reduce((sum, value) => sum + value, 0);
  const level = total <= 7 ? "ok" : total <= 19 ? "warn" : "bad";
  const interpretation = english
    ? total <= 7 ? "mild symptoms — watchful waiting possible" : total <= 19 ? "moderate symptoms — consider medical therapy" : "severe symptoms — treatment and urology opinion recommended"
    : total <= 7 ? "symptômes légers — surveillance possible" : total <= 19 ? "symptômes modérés — traitement médical à discuter" : "symptômes sévères — traitement et avis urologique recommandés";
  const qolText = qol === null ? "" : english
    ? ` Quality of life: ${qol}/6${qol >= 4 ? " (impaired)" : ""}.`
    : ` Qualité de vie : ${qol}/6${qol >= 4 ? " (altérée)" : ""}.`;
  return { band: level, text: `IPSS ${total}/35 — ${interpretation}.${qolText}` };
}

function calculatePsaDensity(values, english) {
  const psa = field(values.psa);
  let volume = field(values.prostateVolume);
  if (volume === null) {
    const length = field(values.prostateLength);
    const width = field(values.prostateWidth);
    const height = field(values.prostateHeight);
    if (length !== null && width !== null && height !== null) volume = length * width * height * 0.52;
  }
  if (psa === null || volume === null || psa < 0 || volume <= 0) return null;
  const density = round(psa / volume, 3);
  const suspicious = density > 0.15;
  return {
    band: suspicious ? "bad" : "ok",
    text: `${english ? "PSA density" : "Densité du PSA"}: ${density} ng/mL/cc (${english ? "volume" : "volume"} ${round(volume)} cc) — ${suspicious ? (english ? "> 0.15: suspicious, consider MRI/biopsy." : "> 0,15 : suspecte, discuter IRM/biopsies.") : (english ? "≤ 0.15: not suspicious in isolation." : "≤ 0,15 : non suspecte isolément.")}`,
  };
}

const INITIAL_VALUES = {
  clAge: "", clWeight: "", clSex: "", clCreat: "", clUnit: "umol",
  kfAge: "", kfSex: "", kfGfr: "", kfAcr: "", kfAcrUnit: "mgmmol",
  psa: "", prostateVolume: "", prostateLength: "", prostateWidth: "", prostateHeight: "",
};

function ToolsTab({ patientMeasurements, patientInfo }) {
  const { i18n } = useTranslation();
  const language = String(i18n.resolvedLanguage || i18n.language || "fr").toLowerCase();
  const english = language.startsWith("en");
  const arabic = language.startsWith("ar");
  const languageIndex = arabic ? 2 : english ? 1 : 0;
  const [activeTool, setActiveTool] = useState("clearance");
  const [values, setValues] = useState(INITIAL_VALUES);
  const [ocrStatus, setOcrStatus] = useState({});
  const [ipssAnswers, setIpssAnswers] = useState(Array(IPSS_QUESTIONS.length).fill(null));
  const [ipssQol, setIpssQol] = useState(null);
  const setValue = (key, value) => setValues((current) => ({ ...current, [key]: value }));
  const patientAge = calculateAge(patientInfo?.birthdate);
  const patientWeight = numberOrNull(patientMeasurements?.weight);
  const patientSex = normalizeSex(patientInfo?.gender);

  useEffect(() => {
    setValues((current) => ({
      ...current,
      clAge: current.clAge || patientAge,
      clWeight: current.clWeight || (patientWeight === null ? "" : String(patientWeight)),
      clSex: current.clSex || patientSex,
      kfAge: current.kfAge || patientAge,
      kfSex: current.kfSex || patientSex,
    }));
  }, [patientAge, patientWeight, patientSex]);

  const simulateOcrExtraction = () => {
    const extracted = {
      clAge: values.clAge || "67",
      clWeight: values.clWeight || "78",
      clSex: values.clSex || "male",
      clCreat: values.clCreat || "165",
      clUnit: values.clCreat ? values.clUnit : "umol",
    };
    setValues((current) => ({ ...current, ...extracted }));
    setOcrStatus({
      ...(values.clAge ? {} : { clAge: "pending" }),
      ...(values.clWeight ? {} : { clWeight: "pending" }),
      ...(values.clSex ? {} : { clSex: "pending" }),
      ...(values.clCreat ? {} : { clCreat: "pending" }),
    });
  };

  const result = useMemo(() => {
    if (activeTool === "clearance") return calculateClearance(values, english);
    if (activeTool === "kfre") return calculateKfre(values, english);
    if (activeTool === "ipss") return calculateIpss(ipssAnswers, ipssQol, english);
    if (activeTool === "psaDensity") return calculatePsaDensity(values, english);
    return null;
  }, [activeTool, values, english, ipssAnswers, ipssQol]);

  const copy = english
    ? {
      title: "Uro-Nephrology tools",
      subtitle: "Clinical scores and calculation tools",
      clearance: "Renal clearance (Cockcroft–Gault · MDRD · CKD-EPI 2021)",
      kfre: "KFRE — Kidney failure risk (Tangri, 4 variables)",
      ipss: "IPSS — International Prostate Symptom Score",
      psaDensity: "PSA density",
      age: "Age (years)", weight: "Weight (kg)", sex: "Sex", creatinine: "Serum creatinine",
      egfr: "Estimated GFR (mL/min/1.73 m²)", acrField: "ACR (albumin/creatinine ratio)",
      psa: "Total PSA (ng/mL)",
      volume: "Prostate volume (cc) — direct", or: "Or estimate using ellipsoid (L × W × H × 0.52):",
      length: "Length (cm)", width: "Width (cm)", height: "Height (cm)",
      optional: " (optional)", ipssNote: "Over the past month:", qolTitle: "Quality of life (not counted in the total)",
      qol: "If you were to spend the rest of your life with your urinary condition just the way it is now, how would you feel?",
      answered: "Answered", of: "of", result: "Result", normal: "Normal / Low", intermediate: "Intermediate",
      high: "High / Severe", disclaimer: "Decision-support tool — does not replace clinical judgment.",
      kfreSource: "Original North American equation (Tangri et al., JAMA 2011; Major et al., PLOS Medicine 2019). Validated for eGFR < 60.",
      ocrExplain: "Age, weight and sex are prefilled from the patient profile when available. The OCR simulation can fill missing values, especially creatinine. Review each value before using the results.",
      simulateOcr: "Simulate extraction", validateOcr: "Validate all extracted values",
      ocrPending: "Extracted — review · Validate", ocrValid: "Validated ✓",
    }
    : {
      title: "Outils uro-néphrologiques",
      subtitle: "Scores et outils de calcul cliniques",
      clearance: "Clairance rénale (Cockcroft–Gault · MDRD · CKD-EPI 2021)",
      kfre: "KFRE — Risque d'insuffisance rénale terminale (Tangri, 4 variables)",
      ipss: "IPSS — Score prostatique international",
      psaDensity: "Densité du PSA",
      age: "Âge (ans)", weight: "Poids (kg)", sex: "Sexe", creatinine: "Créatinine sérique",
      egfr: "DFG estimé (mL/min/1,73 m²)", acrField: "RAC (albuminurie/créatininurie)", psa: "PSA total (ng/mL)",
      volume: "Volume prostatique (cc) — direct", or: "Ou estimation par l'ellipsoïde (L × l × h × 0,52) :",
      length: "Longueur (cm)", width: "Largeur (cm)", height: "Hauteur (cm)",
      optional: "", ipssNote: "Sur le dernier mois:", qolTitle: "Qualité de vie (non comptée dans le total)",
      qol: "Si vous deviez vivre le restant de votre vie avec ces symptômes urinaires, comment le vivriez-vous ?",
      answered: "Réponses", of: "sur", result: "Résultat", normal: "Normal / Faible",
      intermediate: "Intermédiaire", high: "Élevé / Sévère",
      disclaimer: "Outil d'aide à la décision — ne remplace pas le jugement clinique.",
      kfreSource: "Équation nord-américaine originale (Tangri et al., JAMA 2011 ; Major et al., PLOS Medicine 2019). Validée pour un DFG < 60.",
      ocrExplain: "L'âge, le poids et le sexe sont préremplis depuis le dossier patient lorsqu'ils sont disponibles. La simulation OCR peut compléter les valeurs manquantes, notamment la créatinine. Vérifiez les valeurs avant d'utiliser les résultats.",
      simulateOcr: "Simuler une extraction", validateOcr: "Valider toutes les valeurs extraites",
      ocrPending: "Extrait — à vérifier · Valider", ocrValid: "Validé ✓",
    };

  const input = (key, label, type = "number", extra = {}) => {
    const status = activeTool === "clearance" ? ocrStatus[key] : null;
    return (
      <div className="uro-tools__field" key={key}>
        <label htmlFor={`uro-tools-${key}`}>{label}</label>
        <span className="uro-tools__field-control">
          <input
            id={`uro-tools-${key}`}
            type={type}
            value={values[key]}
            className={status ? `uro-tools__ocr-${status}` : ""}
            onChange={(event) => {
              setValue(key, event.target.value);
              if (status) setOcrStatus((current) => ({ ...current, [key]: "pending" }));
            }}
            {...extra}
          />
          {status && (
            <button
              type="button"
              className={`uro-tools__ocr-tag uro-tools__ocr-tag--${status}`}
              onClick={() => setOcrStatus((current) => ({ ...current, [key]: "validated" }))}
              disabled={status === "validated"}
            >
              {status === "pending" ? copy.ocrPending : copy.ocrValid}
            </button>
          )}
        </span>
      </div>
    );
  };
  const select = (key, label, options) => (
    <div className="uro-tools__field" key={key}>
      <label htmlFor={`uro-tools-${key}`}>{label}</label>
      <span className="uro-tools__field-control">
        <select
          id={`uro-tools-${key}`}
          value={values[key]}
          className={activeTool === "clearance" && ocrStatus[key] ? `uro-tools__ocr-${ocrStatus[key]}` : ""}
          onChange={(event) => {
            setValue(key, event.target.value);
            if (activeTool === "clearance" && ocrStatus[key]) {
              setOcrStatus((current) => ({ ...current, [key]: "pending" }));
            }
          }}
        >
          {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        {activeTool === "clearance" && ocrStatus[key] && (
          <button
            type="button"
            className={`uro-tools__ocr-tag uro-tools__ocr-tag--${ocrStatus[key]}`}
            onClick={() => setOcrStatus((current) => ({ ...current, [key]: "validated" }))}
            disabled={ocrStatus[key] === "validated"}
          >
            {ocrStatus[key] === "pending" ? copy.ocrPending : copy.ocrValid}
          </button>
        )}
      </span>
    </div>
  );
  const selectSex = (key) => select(key, copy.sex, sexOptions(english));
  const unitSelect = (key, label, options) => select(key, label, options);
  const renderQuestionnaire = (questions, answers, onAnswer, key, answeredText = true) => {
    const answeredCount = answers.filter((answer) => answer !== null).length;
    return (
      <>
        <div className="uro-tools__questions">
          {questions.map((question, index) => {
            const prompt = question[languageIndex];
            const options = question[3] || IPSS_OPTIONS;
            return (
              <fieldset className="uro-tools__question" key={`${key}-${index}`}>
                <legend>{index + 1}. {prompt}</legend>
                <div className="uro-tools__options">
                  {options.map((option, optionIndex) => (
                    <label className={`uro-tools__option${answers[index] === option[3] ? " is-selected" : ""}`} key={optionIndex}>
                      <input
                        type="radio"
                        name={`${key}-${index}`}
                        checked={answers[index] === option[3]}
                        onChange={() => onAnswer(index, option[3])}
                      />
                      {option[languageIndex]}
                    </label>
                  ))}
                </div>
              </fieldset>
            );
          })}
        </div>
        {answeredText && answeredCount < questions.length && (
          <div className="uro-tools__progress">{copy.answered}: {answeredCount} {copy.of} {questions.length}</div>
        )}
      </>
    );
  };

  const ipssQuestions = IPSS_QUESTIONS.map((question, index) => [
    ...question,
    index === 6 ? NOCTURIA_OPTIONS : IPSS_OPTIONS,
  ]);
  const pageTitle = copy.title;
  const updateAnswer = (index, points, current) => {
    const updated = [...current];
    updated[index] = points;
    return updated;
  };

  return (
    <section className={`uro-tools${arabic ? " is-rtl" : ""}`} aria-label={pageTitle}>
      <header className="uro-tools__header">
        <div className="uro-tools__logo" aria-hidden="true">ML</div>
        <div>
          <h1>{pageTitle}</h1>
          <p>{copy.subtitle}</p>
        </div>
      </header>

      <nav className="uro-tools__tabs" role="tablist" aria-label={pageTitle}>
        {TOOL_TABS.map(([id, fr, en], index) => (
          <button
            key={id}
            type="button"
            role="tab"
            id={`uro-tools-tab-${id}`}
            aria-selected={activeTool === id}
            aria-controls={`uro-tools-panel-${id}`}
            className={activeTool === id ? "is-active" : ""}
            onClick={() => setActiveTool(id)}
          >
            <span>{index + 1}</span>{english ? en : fr}
          </button>
        ))}
      </nav>

      <div
        className="uro-tools__card"
        role="tabpanel"
        id={`uro-tools-panel-${activeTool}`}
        aria-labelledby={`uro-tools-tab-${activeTool}`}
      >
        <h2>{copy[activeTool]}</h2>
        {activeTool === "clearance" && (
          <>
            <div className="uro-tools__ocr-note">{copy.ocrExplain}</div>
            <div className="uro-tools__ocr-actions">
              <button
                type="button"
                className="uro-tools__button uro-tools__button--simulate"
                onClick={simulateOcrExtraction}
              >
                {copy.simulateOcr}
              </button>
              <button
                type="button"
                className="uro-tools__button uro-tools__button--validate"
                onClick={() => setOcrStatus((current) => Object.fromEntries(
                  Object.entries(current).map(([key, status]) => [key, status === "pending" ? "validated" : status]),
                ))}
                disabled={!Object.values(ocrStatus).some((status) => status === "pending")}
              >
                {copy.validateOcr}
              </button>
            </div>
            <div className="uro-tools__fields">
              {input("clAge", copy.age, "number", { min: 1, step: 1 })}
              {input("clWeight", copy.weight, "number", { min: 0, step: 0.5 })}
              {selectSex("clSex")}
              {input("clCreat", copy.creatinine, "number", { min: 0, step: 0.1 })}
              {unitSelect("clUnit", english ? "Creatinine unit" : "Unité de créatinine", [
                { value: "umol", label: "µmol/L" }, { value: "mgL", label: "mg/L" },
              ])}
            </div>
          </>
        )}
        {activeTool === "kfre" && (
          <>
            <div className="uro-tools__fields">
              {input("kfAge", copy.age, "number", { min: 1, step: 1 })}
              {selectSex("kfSex")}
              {input("kfGfr", copy.egfr, "number", { min: 0, step: 0.1 })}
              {input("kfAcr", copy.acrField, "number", { min: 0, step: 0.1 })}
              {unitSelect("kfAcrUnit", english ? "ACR unit" : "Unité du RAC", [
                { value: "mgmmol", label: "mg/mmol" }, { value: "mgg", label: "mg/g" },
              ])}
            </div>
            <p className="uro-tools__hint">{copy.kfreSource}</p>
          </>
        )}
        {activeTool === "ipss" && (
          <>
            <p className="uro-tools__hint">{copy.ipssNote}</p>
            {renderQuestionnaire(ipssQuestions, ipssAnswers, (index, points) =>
              setIpssAnswers((current) => updateAnswer(index, points, current)), "ipss")}
            <div className="uro-tools__subcard">
              <h3>{copy.qolTitle}</h3>
              <p>{arabic
                ? "إذا اضطررت إلى قضاء بقية حياتك مع الأعراض البولية كما هي الآن، كيف ستشعر؟"
                : copy.qol}
              </p>
              <div className="uro-tools__options">
                {QOL_OPTIONS.map((option) => (
                  <label className={`uro-tools__option${ipssQol === option[3] ? " is-selected" : ""}`} key={option[3]}>
                    <input type="radio" name="ipss-qol" checked={ipssQol === option[3]} onChange={() => setIpssQol(option[3])} />
                    {option[languageIndex]}
                  </label>
                ))}
              </div>
            </div>
          </>
        )}
        {activeTool === "psaDensity" && (
          <>
            <div className="uro-tools__fields">
              {input("psa", copy.psa, "number", { min: 0, step: 0.1 })}
              {input("prostateVolume", copy.volume, "number", { min: 0, step: 0.5 })}
            </div>
            <p className="uro-tools__hint">{copy.or}</p>
            <div className="uro-tools__fields">
              {input("prostateLength", copy.length, "number", { min: 0, step: 0.1 })}
              {input("prostateWidth", copy.width, "number", { min: 0, step: 0.1 })}
              {input("prostateHeight", copy.height, "number", { min: 0, step: 0.1 })}
            </div>
          </>
        )}
        {result && (
          <div className={`uro-tools__result uro-tools__result--${result.band}`} role="status">
            <strong>{result.band === "ok" ? copy.normal : result.band === "warn" ? copy.intermediate : copy.high}</strong>
            <p>{result.text}</p>
          </div>
        )}
      </div>

      <p className="uro-tools__disclaimer">⚠️ {copy.disclaimer}</p>
    </section>
  );
}

export default ToolsTab;
